import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import format from 'string-format';
import axios from 'axios';
import Avatar from '../Avatar';
import UserAccount from '../../../API/UserAccount';
import resx from '../../../utilities/resx';
import Image from '../Image';
import TrackPhoneNumber from '../TrackPhoneNumber';
import EnhancedProfile from './EnhancedProfile';
import GeneralUtility from '../../../utilities/generalUtility';
import { RESX_GLOBAL } from '../../../Constants';
import { withMessage } from '../Message';

import ConnectButton from '../ConnectButton';

const ProfileSideBarImpl = props => {
    const user = props.user;
    const viewer = {
        isProfessional:
            _path(['RESAAS', 'User2', 'IsProfessional'], window) &&
            !_path(['RESAAS', 'User2', 'IsPendingProfessional'], window),
        userId: _path(['RESAAS', 'User2', 'UserId'], window),
        isOwner: user.isOwner
    };
    const [logo, setLogo] = useState(null);
    const [enhancedProfile, setEnhancedProfile] = useState(null);

    // initialize
    useEffect(() => {

        // query logo
        const getAccountJson = UserAccount.getAccountJson(user.userId);
        getAccountJson.request
            .then(response => {
                // set adhoc values
                if (response.data) {
                    const profile = response.data;
                    setLogo(profile.LogoUrl || '');

                    if (profile.StartedIn) {
                        const yearsExp = new Date().getFullYear() - parseInt(profile.StartedIn, 10);
                        profile.YearsExpStr =
                            yearsExp > 1
                            ? format(resx(RESX_GLOBAL, 'USER_YEARS_EXP'),
                                    yearsExp
                                )
                                : '1 Year of Experience';
                    }

                    setEnhancedProfile(profile);
                } else {
                    setLogo('');
                }
            })
            .catch(error => {
                if (axios.isCancel(error)) {
                    return;
                }
                GeneralUtility.logWebRequestFailure(error, `fetch UserInfoService from ProfileSideBar`);
            });

        return () => {
            getAccountJson.cancel();
        };
    }, []);


    return (
        <React.Fragment>
            <div id="profile_nav_module">
                <div
                    className="inner"
                    itemScope=""
                    itemType="http://schema.org/RealEstateAgent"
                    style={{ position: 'relative' }}
                >
                    {/* Profile Photo */}
                    <a href={`${user.domainurl}/profile`}>
                        <Avatar
                            websiteId={parseInt(user.websiteId, 10)}
                            imageAttributes={{ itemProp: 'image' }}
                            imgClassName={'profile_pic'}
                        />
                    </a>

                    <div className="component-user-details user_details">
                        {/* User Name */}
                        <h2 className="user_name_heading user-name re-text-lg notranslate" itemProp="name">
                            <a href={`${user.domainurl}/profile`}>
                                <span>{user.userFirstName}</span> <span>{user.userLastName}</span>
                            </a>
                        </h2>

                        {/* Premium Badge */}
                        <div className="premium-badge-profile-container">
                            <a href="/pricing" target="_blank">
                                {user.premiumUser && !user.premiumPlus ? (
                                    <React.Fragment>
                                        <span className="premium-badges-silver-small"></span>
                                        <span>{resx(RESX_GLOBAL, 'PREMIUM_TEXT')}</span>
                                    </React.Fragment>
                                ) : null}
                                {user.premiumUser && user.premiumPlus ? (
                                    <React.Fragment>
                                        <span className="premium-badges-silver-small re-premiumplus"></span>
                                        <span>{resx(RESX_GLOBAL, 'PREMIUM_PLUS_TEXT')}</span>
                                    </React.Fragment>
                                ) : null}
                            </a>
                        </div>

                        {/* Office Name */}
                        {user.office.name && (
                            <p
                                className="profile-details-office"
                                itemProp="memberOf"
                                itemScope=""
                                itemType="http://schema.org/Organization"
                            >
                                <span itemProp="name">{user.office.name}</span>
                            </p>
                        )}

                        {/* Address */}
                        <address
                            className="user-address"
                            itemProp="address"
                            itemScope=""
                            itemType="http://schema.org/PostalAddress"
                        >
                            {user.office.address && (
                                <p className="street-address" itemProp="streetAddress">
                                    {user.office.address}
                                </p>
                            )}
                            {user.office.city && user.office.state && (
                                <p className="profile-city-state">
                                    <span itemProp="addressLocality">{user.office.city}</span>,{' '}
                                    <span itemProp="addressRegion">{user.office.state}</span>
                                </p>
                            )}
                            {!user.office.city && user.office.state && (
                                <p className="display-inline state" itemProp="addressRegion">
                                    {user.office.state}
                                </p>
                            )}
                            {user.office.postcode && (
                                <p className="postal-code" itemProp="postalCode">
                                    {user.office.postcode}
                                </p>
                            )}
                        </address>

                        {/* Phone Numbers, WebsiteUrl, Firm Link */}
                        <ul className="user-additional-information details-list clearfix">
                            {user.userWebsiteUrl && (
                                <li>
                                    <i className="re-icon re-icon-user" />
                                    <a
                                        className="user-website"
                                        href={user.userWebsiteUrl}
                                        title={user.userWebsiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        itemProp="url"
                                    >
                                        {' '}
                                        {user.userWebsiteUrl}{' '}
                                    </a>
                                </li>
                            )}

                            {user.firm.FirmUniqueName && !user.firm.HideFirmFeed && !user.firm.HasFirmBranding && (
                                <li
                                    className="firm-link"
                                    itemProp="memberOf"
                                    itemScope
                                    itemType="http://schema.org/Organization"
                                >
                                    <a
                                        className="user-firm"
                                        href={`/agents/firm/${user.firm.FirmUniqueName}/?_subjectName=${user.firm.FirmName}`}
                                        itemProp="url"
                                    >
                                        <span itemProp="name" className="notranslate">{user.firm.FirmName}</span>
                                    </a>
                                </li>
                            )}
                        </ul>
                        {/* enhanced profile information */}
                        <EnhancedProfile enhancedProfile={enhancedProfile} bioInfo={user.bioInfo} />
                        {/* Connect Button */}
                        <ConnectButton
                            displayName={`${user.userFirstName} ${user.userLastName}`}
                            mixpanelElement="profile side bar"
                            userId={user.userId}
                        />

                        {/* Referral Button */}
                        {user.canUserRefer ? (
                            <div className="referral-link">
                                <a
                                    href={viewer.isOwner ? "#" : `${user.domainurl}/referral`}
                                    onClick={(e) => {
                                        if (viewer.isOwner) {
                                            e.preventDefault();
                                            props.message.addMessage({ message: resx(RESX_GLOBAL, 'USER_VIEW_ITS_OWN_PROFILE'), type: 'success' });
                                        }
                                    }}
                                    className="re-btn re-btn-subtle re-btn-small re-btn-full-width"
                                    title={resx(RESX_GLOBAL, 'REFERRAL_SEND_REFERRAL_BUTTON')}
                                    data-mixpanel-name="Intent"
                                    data-mixpanel-type="Send Private Referral"
                                >
                                    {resx(RESX_GLOBAL, 'REFERRAL_SEND_REFERRAL_BUTTON')}
                                </a>
                            </div>
                        ) : null}

                        {/* Message Button */}
                        {viewer.isProfessional && (
                            <div className="contact_user contact-user-bottom">
                                <a
                                    href={`/inbox?UserId=${user.userId}&WebsiteId=${user.websiteId}&FirstName=${user.userFirstName}&LastName=${user.userLastName}`}
                                    className="re-btn re-btn-subtle re-btn-small re-btn-full-width"
                                    data-mixpanel-name="Intent"
                                    data-mixpanel-type="Start a Conversation Button"
                                >
                                    {resx(RESX_GLOBAL, 'SENDIMBUTTON_TEXT')}
                                </a>
                            </div>
                        )}
                        
                        {viewer.userId && user.userPhone ? (
                            <div className="contact_user contact-user-bottom">
                                <TrackPhoneNumber
                                    phoneNumber={user.userPhone}
                                    dataContext="profile sidebar"
                                    userId={user.userId}
                                />
                            </div>
                        ) : null}

                        {viewer.userId && user.office.phone && !user.userPhone ? (
                            <div className="contact_user contact-user-bottom">
                                <TrackPhoneNumber
                                    phoneNumber={user.office.phone}
                                    dataContext="profile sidebar"
                                    userId={user.userId}
                                    btnText={'CALL MY OFFICE'}
                                />
                            </div>
                        ) : null}

                        {/* Twitter Facebook Linkedin */}
                        {user.premiumUser && (
                            <div className="social_container social-tools-wrapper">
                                <div className="social-tools">
                                    {user.social.FacebookUrl && (
                                        <a
                                            href={user.social.FacebookUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="re-btn re-btn-round re-btn-round-small re-icon re-icon-facebook"
                                            data-mixpanel-name="Intent"
                                            data-mixpanel-type="enhanced-profile-social-media"
                                            data-mixpanel-url="facebook"
                                            data-testid="facebook"
                                            itemProp="sameas"
                                        />
                                    )}
                                    {user.social.TwitterUrl && (
                                        <a
                                            href={user.social.TwitterUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="re-btn re-btn-round re-btn-round-small re-icon re-icon-twitter"
                                            data-mixpanel-name="Intent"
                                            data-mixpanel-type="enhanced-profile-social-media"
                                            data-mixpanel-url="twitter"
                                            data-testid="twitter"
                                            itemProp="sameas"
                                        />
                                    )}
                                    {user.social.LinkedInUrl && (
                                        <a
                                            href={user.social.LinkedInUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="re-btn re-btn-round re-btn-round-small re-icon re-icon-linkedin"
                                            data-mixpanel-name="Intent"
                                            data-mixpanel-type="enhanced-profile-social-media"
                                            data-mixpanel-url="linkedin"
                                            data-testid="linkedin"
                                            itemProp="sameas"
                                        />
                                    )}
                                    {user.social.InstagramUrl && (
                                        <a
                                            href={user.social.InstagramUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="re-btn re-btn-round re-btn-round-small re-icon re-icon-instagram"
                                            data-mixpanel-name="Intent"
                                            data-mixpanel-type="enhanced-profile-social-media"
                                            data-mixpanel-url="instagram"
                                            data-testid="instagram"
                                            itemProp="sameas"
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Logo */}
                        <div className="text-center">
                            {logo && user.premiumUser && (
                                <Image className="re-logo-container" src={logo} alt="logo" width={115} />
                            )}
                        </div>
                        {logo === '' && viewer.isOwner ? (
                            <div className="re-no-logo">
                                <div className="re-no-logo-content">
                                    <a
                                        href="/myaccount#uploadlogo"
                                        data-mixpanel-name="Intent"
                                        data-mixpanel-type="user-uploads-logo-clicked"
                                    >
                                        <span className="re-icon re-icon-upload" />
                                        <div>{resx(RESX_GLOBAL, 'USER_UPLOAD_LOGO')}</div>
                                    </a>
                                </div>
                            </div>
                        ) : null}

                        {/* Groups Joined */}
                        {user.groups && user.groups.length > 0 && (
                            <div className="target-user-groups clearfix">
                                <span className="groups-joined-header">
                                    <p>{resx(RESX_GLOBAL, 'GROUPS_HEADER')}</p>
                                </span>
                                {user.groups.map(group => (
                                    <li
                                        data-id={group.GroupId}
                                        data-name={group.GroupName}
                                        title={group.GroupName}
                                        key={group.GroupId}
                                    >
                                        <img src={group.GroupLogoUrl} />
                                        <p>{group.GroupName}</p>
                                    </li>
                                ))}
                            </div>
                        )}

                        {/* Edit Profile Button */}
                        {viewer.isOwner && (
                            <a className="edit-profile-link re-btn re-btn-full-width" href="/myaccount">
                                {resx(RESX_GLOBAL, 'EDIT_PROFILE')}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

ProfileSideBarImpl.propTypes = {
    user: PropTypes.object
};

export default withMessage(ProfileSideBarImpl);
